import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Descriptions,
  Drawer,
  Empty,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
  DatePicker,
} from 'antd';
import { ReloadOutlined, DownloadOutlined, ClearOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAdminSocketContext } from '../../sockets/AdminSocketContext';
import UserDetailsDrawer from './UserDetailsDrawer';
import {
  downloadCsv,
  formatDateTime,
  formatRelativeTime,
  statusMeta,
  todayFilename,
} from './adminFormatters';
import {
  getRequestGroupKey,
  getRequestGroupLabel,
  requestTypeLabel,
} from '../../utils/requestTypeLabels';
import './adminLayout.sass';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const AdminRequests = () => {
  const { listRequests, getRequest, getFiltersMeta, getUser, onlineUserIds } = useAdminSocketContext();
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [requestType, setRequestType] = useState(null);
  const [userId, setUserId] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [meta, setMeta] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null);

  useEffect(() => {
    getFiltersMeta()
      .then((res) => setMeta(res.meta))
      .catch(() => {});
  }, [getFiltersMeta]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listRequests({
        status: status || null,
        requestType: requestType || null,
        userId: userId ?? null,
        dateFrom: dateRange?.[0]?.startOf('day')?.toISOString() ?? null,
        dateTo: dateRange?.[1]?.endOf('day')?.toISOString() ?? null,
        limit: 100,
      });
      setTasks(res.tasks || []);
      setTotal(res.total || 0);
    } catch (e) {
      message.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [listRequests, status, requestType, userId, dateRange]);

  useEffect(() => {
    load();
  }, [load]);

  const resetFilters = () => {
    setStatus(null);
    setRequestType(null);
    setUserId(null);
    setDateRange(null);
  };

  const filtersActive = Boolean(status || requestType || userId || dateRange);

  const openDetail = async (taskId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const res = await getRequest(taskId);
      setDetail(res);
    } catch (e) {
      message.error(e.message);
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleExport = () => {
    downloadCsv(
      todayFilename('requests'),
      [
        { title: 'ID задачи', dataIndex: 'id' },
        { title: 'Пользователь', dataIndex: 'username' },
        { title: 'User ID', dataIndex: 'user_id' },
        { title: 'Sub-запросов', dataIndex: 'requests_count' },
        { title: 'Создана', value: (r) => formatDateTime(r.created_at) },
        { title: 'Обновлена', value: (r) => formatDateTime(r.updated_at) },
      ],
      tasks
    );
    message.success(`Экспортировано запросов: ${tasks.length}`);
  };

  const statusOptions = (meta?.statuses || []).map((value) => ({
    value,
    label: statusMeta(value).label,
  }));

  const requestTypeOptions = (meta?.request_types || []).map((value) => ({
    value,
    label: `${getRequestGroupLabel(getRequestGroupKey(value))} · ${value}`,
  }));

  const columns = [
    {
      title: 'ID задачи',
      dataIndex: 'id',
      width: 150,
      render: (id) => (
        <Tooltip title={id}>
          <Text code copyable={{ text: id, tooltips: ['Скопировать ID', 'Скопировано'] }} style={{ fontSize: 12 }}>
            {id.slice(0, 8)}…
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Пользователь',
      dataIndex: 'username',
      width: 160,
      render: (username, row) => (
        <button
          type="button"
          className="admin-user-link"
          onClick={() => setProfileUserId(row.user_id)}
        >
          {username}
        </button>
      ),
    },
    {
      title: 'Sub-запросов',
      dataIndex: 'requests_count',
      width: 120,
      align: 'center',
      sorter: (a, b) => (a.requests_count || 0) - (b.requests_count || 0),
    },
    {
      title: 'Создана',
      dataIndex: 'created_at',
      width: 160,
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      render: (value) => (
        <Tooltip title={formatDateTime(value)}>
          <span>{formatRelativeTime(value)}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Обновлена',
      dataIndex: 'updated_at',
      width: 160,
      render: (value) => (
        <Tooltip title={formatDateTime(value)}>
          <span>{formatRelativeTime(value)}</span>
        </Tooltip>
      ),
    },
    {
      title: '',
      key: 'act',
      width: 100,
      render: (_, row) => (
        <Button size="small" onClick={() => openDetail(row.id)}>
          Детали
        </Button>
      ),
    },
  ];

  const detailRequests = detail?.requests || [];
  const statusSummary = detailRequests.reduce((acc, row) => {
    const key = row.status || 'unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  return (
    <>
      <Card size="small" className="admin-filters-card">
        <Space wrap size="middle" align="start" className="admin-filters-card__space" style={{ width: '100%' }}>
          <Select
            allowClear
            showSearch
            placeholder="Пользователь"
            style={{ minWidth: 180 }}
            value={userId}
            onChange={setUserId}
            optionFilterProp="label"
            options={(meta?.users || []).map((u) => ({
              value: u.id,
              label: `${u.username} (#${u.id})`,
            }))}
          />
          <Select
            allowClear
            placeholder="Тип запроса"
            style={{ minWidth: 260 }}
            value={requestType}
            onChange={setRequestType}
            options={requestTypeOptions}
          />
          <Select
            allowClear
            placeholder="Статус"
            style={{ width: 160 }}
            value={status}
            onChange={setStatus}
            options={statusOptions}
          />
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            format="DD.MM.YYYY"
            presets={[
              { label: 'Сегодня', value: [dayjs().startOf('day'), dayjs()] },
              { label: '7 дней', value: [dayjs().subtract(7, 'd'), dayjs()] },
              { label: '30 дней', value: [dayjs().subtract(30, 'd'), dayjs()] },
            ]}
          />
          <Button type="primary" icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Применить
          </Button>
          {filtersActive && (
            <Button icon={<ClearOutlined />} onClick={resetFilters}>
              Сбросить
            </Button>
          )}
          <Button icon={<DownloadOutlined />} onClick={handleExport} disabled={!tasks.length}>
            CSV
          </Button>
        </Space>
      </Card>

      <Space size={12} wrap className="admin-list-meta">
        <span>Найдено: {total}</span>
        {total > tasks.length && (
          <span className="admin-list-meta__muted">
            показаны последние {tasks.length}
          </span>
        )}
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={tasks}
        columns={columns}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (count) => `Запросов: ${count}`,
        }}
        scroll={{ x: 1000 }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={filtersActive ? 'По фильтрам ничего не найдено' : 'Запросов пока нет'}
            />
          ),
        }}
      />

      <Drawer
        title="Детали задачи"
        width={Math.min(900, typeof window !== 'undefined' ? window.innerWidth - 24 : 900)}
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setDetail(null);
        }}
      >
        <Spin spinning={detailLoading}>
          {detail?.task ? (
            <>
              <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label="ID">
                  <Text code copyable={{ text: detail.task.id }}>
                    {detail.task.id}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Пользователь">
                  {detail.task.username} (#{detail.task.user_id})
                </Descriptions.Item>
                <Descriptions.Item label="Создана">
                  <Tooltip title={formatDateTime(detail.task.created_at)}>
                    <span>
                      {formatDateTime(detail.task.created_at)} · {formatRelativeTime(detail.task.created_at)}
                    </span>
                  </Tooltip>
                </Descriptions.Item>
                <Descriptions.Item label="Обновлена">
                  {formatDateTime(detail.task.updated_at)} · {formatRelativeTime(detail.task.updated_at)}
                </Descriptions.Item>
                <Descriptions.Item label="Статусы sub-запросов">
                  {Object.keys(statusSummary).length ? (
                    <Space wrap size={4}>
                      {Object.entries(statusSummary).map(([value, count]) => {
                        const meta = statusMeta(value);
                        return (
                          <Tag key={value} color={meta.color}>
                            {meta.label}: {count}
                          </Tag>
                        );
                      })}
                    </Space>
                  ) : (
                    '—'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Описание (JSON)">
                  <pre className="admin-json-view">
                    {JSON.stringify(detail.task.description, null, 2)}
                  </pre>
                </Descriptions.Item>
              </Descriptions>

              <h4 style={{ marginTop: 16 }}>Sub-requests ({detailRequests.length})</h4>
              <Table
                size="small"
                rowKey="id"
                loading={detailLoading}
                dataSource={detailRequests}
                pagination={false}
                scroll={{ x: 760 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sub-запросов нет" /> }}
                columns={[
                  {
                    title: 'Тип',
                    dataIndex: 'request_type',
                    width: 180,
                    render: (value) => (
                      <Tooltip title={value}>
                        <span>{requestTypeLabel(value)}</span>
                      </Tooltip>
                    ),
                  },
                  {
                    title: 'Статус',
                    dataIndex: 'status',
                    width: 130,
                    render: (value) => {
                      const meta = statusMeta(value);
                      return <Tag color={meta.color}>{meta.label}</Tag>;
                    },
                  },
                  { title: 'Pipeline', dataIndex: 'pipeline_stage', width: 130, render: (v) => v || '—' },
                  {
                    title: 'External ID',
                    dataIndex: 'external_task_id',
                    ellipsis: true,
                    render: (v) =>
                      v ? (
                        <Text code copyable={{ text: v }} style={{ fontSize: 11 }}>
                          {v}
                        </Text>
                      ) : (
                        '—'
                      ),
                  },
                  {
                    title: 'Создан',
                    dataIndex: 'created_at',
                    width: 150,
                    render: (value) => (
                      <Tooltip title={formatDateTime(value)}>
                        <span>{formatRelativeTime(value)}</span>
                      </Tooltip>
                    ),
                  },
                  {
                    title: 'Результат',
                    dataIndex: 'result',
                    width: 100,
                    render: (result) =>
                      result ? <Tag color="green">есть</Tag> : <Tag>нет</Tag>,
                  },
                ]}
              />
            </>
          ) : (
            !detailLoading && (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Не удалось загрузить задачу" />
            )
          )}
        </Spin>
      </Drawer>

      <UserDetailsDrawer
        open={profileUserId != null}
        userId={profileUserId}
        onClose={() => setProfileUserId(null)}
        getUser={getUser}
        isOnline={(onlineUserIds || []).map(Number).includes(Number(profileUserId))}
      />
    </>
  );
};

export default AdminRequests;
